/**
 * PicklistRow - Single picklist row with verification status
 *
 * Shows green checkmark when item is picked (verified via scan).
 * Also supports manual checkbox toggle for digital picking.
 * Touch-friendly row height (h-16, p-4) for warehouse tablets.
 *
 * The product cell carries ONE inline photo of the thing the picker walks to
 * the shelf to fetch (`PicklistPickImage`): the blank base garment on a POD
 * row, the finished good on a P&P row. Unlike the station screens this is the
 * catalogue photo attached to that SKU in the ERP, never a garment mockup.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, RotateCcw, ArrowUpCircle, Layers, Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ProductionPreviewTile,
  buildPreviewSourceChain,
  thumbnailFirstChain,
} from '@/components/production/ProductionPreviewTile';
import { ClientLabelledBadge } from '@/components/production/ClientLabelledBadge';
import { cn } from '@/lib/utils';
import { picklistDisplayName, isBackupGroup } from '../utils/picklist-grouping';

interface PicklistRowProps {
  group: {
    groupKey: string;
    /** The SKU the picker takes (the backup's when the group drew on one). */
    sku: string;
    /** The ORDER's SKU; differs from `sku` when the picker takes a backup. */
    unitSku: string;
    /** The shirt carries a client's neck label: shows the client badge. */
    clientLabelled: boolean;
    clientName: string | null;
    /**
     * Original Shopify SKU (preserves `2`-prefix and `CUST-` wrapper that
     * are stripped from `sku` by normalization). Required for the BLU-435
     * DUAL/CUSTOM chip detection. Null when not plumbed by caller.
     */
    shopifySku?: string | null;
    productName: string;
    size: string;
    color: string;
    quantity: number;
    /** Genuine redos only; order-change units are in `orderChangeCount`. */
    redoCount: number;
    /** Order-change replacement units in this group. */
    orderChangeCount: number;
    fulfillmentType: string;
    /** 'DTG' | 'DTF' — drives the fuchsia DTF chip on POD rows. */
    printTechnology?: string;
    shopifyProductType: string | null;
    priorityInjectionCount?: number;
    /**
     * Catalogue photo of what the picker fetches (blank for POD, finished
     * good for P&P). Null or absent renders the designed empty frame.
     */
    pickImageUrl?: string | null;
  };
  pickedCount: number;
  isEven: boolean;
  /** Optional product type alias map: Shopify type → display name */
  productTypeAliases?: Record<string, string>;
  /** Manual checkbox state (local, not persisted) */
  isChecked?: boolean;
  /** Toggle manual checkbox */
  onToggle?: () => void;
}

// BLU-435: derive CUSTOM/DUAL signals from existing data (no new backend call).
// CUST- prefix survives in BOTH the normalized `sku` field and `shopifySku`,
// because normalization unwraps it. The `2`-prefix is stripped from `sku` but
// preserved in `shopifySku`, so we MUST consult shopifySku for the DUAL chip.
function isCustomSku(sku: string | null | undefined): boolean {
  return !!sku && sku.startsWith('CUST-');
}

function isStandardDualSku(sku: string | null | undefined): boolean {
  if (!sku) return false;
  const firstSegment = sku.split('-')[0] ?? '';
  return /^2[A-Z]/i.test(firstSegment);
}

/** Source size for the 48px screen tile: 2x for high-density displays. */
export const PICKLIST_SCREEN_THUMB_PX = 96;

interface PicklistPickImageProps {
  /** Catalogue photo of what the picker fetches. Null renders the empty frame. */
  pickImageUrl: string | null | undefined;
  /** The row's display name (`picklistDisplayName`). With size and SKU, it
   *  names what the photo is OF. */
  displayName: string;
  size: string;
  sku: string;
}

/**
 * The ONE inline photo on a picklist row, shared by both row renderers
 * (this component and the read-only row in BatchDetailScreen) so the two can
 * never drift.
 *
 * A single `catalog` photo and nothing behind it: no fallback to the other
 * fulfilment type's photo, no fallback to a printed product. The tile asks for
 * a 96px copy (2x the 48px tile) and falls back to the original photo only if
 * that copy fails. The copy carries its original as the full-size twin for the
 * hover zoom. A missing photo renders the tile's dashed empty frame. No
 * `onOpen`: there is no lightbox on this screen. `captionMode="none"` is
 * load-bearing, not cosmetic: the reserved caption slot would add 20px and
 * push the row past h-16.
 */
export function PicklistPickImage({ pickImageUrl, displayName, size, sku }: PicklistPickImageProps) {
  const entry = useMemo(
    () => ({
      kind: 'product' as const,
      sources: thumbnailFirstChain(
        buildPreviewSourceChain([[pickImageUrl, 'catalog']]),
        PICKLIST_SCREEN_THUMB_PX,
      ),
    }),
    [pickImageUrl],
  );
  return (
    <ProductionPreviewTile
      entry={entry}
      subject={`${displayName} ${size} ${sku}`.replace(/\s+/g, ' ').trim()}
      variant="list"
      captionMode="none"
    />
  );
}

export function PicklistRow({ group, pickedCount, isEven, productTypeAliases, isChecked, onToggle }: PicklistRowProps) {
  const { t } = useTranslation('production');
  const allPicked = pickedCount >= group.quantity;
  const partialPicked = pickedCount > 0 && pickedCount < group.quantity;

  // BLU-435 chip detection (mutually exclusive in practice — CUST- and 2-
  // prefixed standard SKUs don't overlap in our taxonomy).
  // CUSTOM works from either field; DUAL must use shopifySku because the
  // 2-prefix is stripped during normalization for inventory matching.
  const skuForCustom = group.shopifySku ?? group.sku;
  const customChip = isCustomSku(skuForCustom);
  const dualChip = !customChip && isStandardDualSku(group.shopifySku);
  const displayName = picklistDisplayName(group, productTypeAliases);

  return (
    <tr
      className={cn(
        'h-16 transition-colors', // h-16 for warehouse touch targets
        onToggle && 'cursor-pointer',
        isEven ? 'bg-card' : 'bg-muted/30',
        (allPicked || isChecked) && 'bg-green-50 dark:bg-green-950/20'
      )}
      onClick={onToggle}
    >
      {/* Verification status - w-16 for touch target */}
      <td className="px-4 py-4 text-center w-16">
        {allPicked ? (
          <Check className="h-6 w-6 text-green-600 mx-auto" aria-label={t('batch.detail.picked')} />
        ) : partialPicked ? (
          <span className="text-base font-medium text-amber-600">
            {pickedCount}/{group.quantity}
          </span>
        ) : (
          <Checkbox
            checked={isChecked ?? false}
            onCheckedChange={onToggle}
            onClick={(e) => e.stopPropagation()}
            className="h-5 w-5"
            aria-label={t('batch.detail.markAsPicked', { sku: group.sku, size: group.size })}
          />
        )}
      </td>
      <td className="px-4 py-4 text-base font-semibold">
        {group.quantity}
      </td>
      {/* py-2, not py-4: the 48px pick photo plus 16px of padding keeps the
          row at exactly h-16. */}
      <td className="px-4 py-2 text-base">
        <div className="flex items-center gap-3">
          <PicklistPickImage
            pickImageUrl={group.pickImageUrl}
            displayName={displayName}
            size={group.size}
            sku={group.sku}
          />
          <span className="inline-flex items-center gap-2">
            {displayName}
            {group.redoCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                <RotateCcw className="h-3 w-3" aria-hidden="true" />
                {t('batch.detail.redo', { count: group.redoCount })}
              </span>
            )}
            {group.orderChangeCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-400 px-2 py-0.5 text-xs font-medium text-amber-700 dark:border-amber-600 dark:text-amber-300">
                <Plus className="h-3 w-3" aria-hidden="true" />
                {t('batch.detail.orderChange', { count: group.orderChangeCount })}
              </span>
            )}
            {/* Palette claim: sky-100 is reserved for priority-injection / requeue
                signal on the picklist surface. The print-station Back-only banner
                also uses the sky family (bg-sky-50 + text-sky-900) but on a
                different surface (PrintStationPage's PrintSideIndicator). The two
                never co-render; new chip authors should pick a different family
                before adding a third sky consumer to avoid latent collision. */}
            {(group.priorityInjectionCount ?? 0) > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">
                <ArrowUpCircle className="h-3 w-3" aria-hidden="true" />
                {t('batch.detail.requeued', { count: group.priorityInjectionCount })}
              </span>
            )}
            {/* DTF chip — fuchsia (the POD·DTF section accent), mirrors the
                printed GarmentLabel's DTF indicator so screen matches label.
                DTG carries no chip (the default tech; the purple section header
                already says it). */}
            {group.fulfillmentType !== 'P&P' && group.printTechnology === 'DTF' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-100 px-2 py-0.5 text-xs font-medium text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300">
                {t('picking.badges.dtf')}
              </span>
            )}
            {/* BLU-435: CUSTOM chip — violet utility, NOT brand-primary, so a
                custom-heavy batch doesn't read as "everything is branded." */}
            {customChip && (
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                {t('picking.badges.custom')}
              </span>
            )}
            {/* BLU-435: DUAL chip — muted with Layers icon. Mirrors the
                print-station banner's brand-accent dual indicator at small size. */}
            {dualChip && (
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
                <Layers className="h-3 w-3" aria-hidden="true" />
                {t('picking.badges.dual')}
              </span>
            )}
            {group.clientLabelled && <ClientLabelledBadge clientName={group.clientName} />}
          </span>
        </div>
      </td>
      {/* Size text larger for warehouse visibility */}
      <td className="px-4 py-4 text-xl font-bold">{group.size}</td>
      <td className="px-4 py-4 text-base">{group.color}</td>
      <td className={cn('px-4 py-4 text-base font-mono', isBackupGroup(group) && 'font-bold')}>{group.sku}</td>
    </tr>
  );
}
